import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ChevronDown, Cloud, Menu, ShieldCheck, Sparkles, X } from 'lucide-react';
import { normalizeSafeUrl, sanitizeHtml } from '@/lib/sanitizeHtml';
import { getSectionStylesForBreakpoint, useResponsiveBreakpoint } from '@/lib/responsiveStyles';

const isAlternate = variant => /Centered|Operations|Checklist|Featured|Standards|Split Panel|Insights List|Service Story|Guided|Compact Brand/.test(variant || '');
const cardText = card => (
  <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.text || card.answer || card.summary || card.date || '') }} />
);

function CardImage({ card, className = 'mb-4 max-h-48 w-full rounded-xl object-cover' }) {
  if (!card.image) return null;
  const width = Number(card.imageWidth);
  const height = Number(card.imageHeight);
  const hasCustomSize = Number.isFinite(width) && width > 0 || Number.isFinite(height) && height > 0;
  return (
    <img
      src={normalizeSafeUrl(card.image)}
      alt={card.imageAlt || card.title || ''}
      loading="lazy"
      className={className}
      style={{
        width: Number.isFinite(width) && width > 0 ? `${Math.min(width, 1600)}px` : undefined,
        height: Number.isFinite(height) && height > 0 ? `${Math.min(height, 1200)}px` : undefined,
        maxHeight: hasCustomSize ? 'none' : undefined,
      }}
    />
  );
}

function CardIcon({ card, className = '' }) {
  const width = Number(card.iconWidth);
  const height = Number(card.iconHeight);
  return (
    <span
      className={className}
      style={{
        width: Number.isFinite(width) && width > 0 ? `${Math.min(width, 500)}px` : undefined,
        height: Number.isFinite(height) && height > 0 ? `${Math.min(height, 500)}px` : undefined,
        fontSize: Number.isFinite(height) && height > 0 ? `${Math.min(height * 0.6, 300)}px` : undefined,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.icon || '•') }}
    />
  );
}

function ActionLink({ children, href = '#contact', secondary = false }) {
  return (
    <a href={normalizeSafeUrl(href)} className={`inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition ${secondary ? 'border border-white/25 bg-white/5 text-white hover:bg-white/10' : 'bg-blue-600 text-white shadow-lg shadow-blue-950/20 hover:bg-blue-500'}`}>
      {children}<ArrowUpRight className="h-4 w-4" />
    </a>
  );
}

function HeaderSection({ section }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigation = (section.navigation || []).filter(item => item.label);
  const actions = (section.actions || []).filter(item => item.label);
  const variant = section.variant || '';
  const centeredBrand = variant === 'Centered Brand';
  const centeredNavigation = variant === 'Centered Navigation';
  const splitNavigation = variant === 'Split Navigation';
  const announcement = variant === 'Announcement Bar';
  const mobileFirst = variant === 'Mobile-first Header';
  const logo = section.brandLogo
    ? <img src={normalizeSafeUrl(section.brandLogo)} alt={section.brandName || ''} className="max-h-10 max-w-48 object-contain" />
    : <span className="text-lg font-bold text-slate-900">{section.brandName || ''}</span>;
  const renderNavigation = mobile => (
    <nav aria-label={mobile ? 'Mobile navigation' : 'Main navigation'} className={mobile ? 'flex flex-col items-stretch gap-1' : `hidden items-center gap-5 md:flex ${centeredNavigation ? 'flex-1 justify-center' : splitNavigation ? 'flex-1 justify-start' : ''}`}>
      {navigation.map((item, index) => (
        <a key={`${item.href}-${index}`} href={normalizeSafeUrl(item.href || '#')} onClick={() => setMenuOpen(false)} className="py-2 text-sm font-medium text-slate-700 transition hover:text-blue-700">
          {item.label}
        </a>
      ))}
      {mobile && renderActions(true)}
    </nav>
  );
  const renderActions = mobile => (
    <div className={`flex flex-wrap gap-2 ${mobile ? 'pt-2' : 'hidden md:flex'} ${centeredBrand && !mobile ? 'w-full justify-center' : ''}`}>
      {actions.map((item, index) => (
        <a
          key={`${item.href}-${index}`}
          href={normalizeSafeUrl(item.href || '#')}
          onClick={() => setMenuOpen(false)}
          className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition ${
            item.style === 'text'
              ? 'text-slate-700 hover:text-blue-700'
              : item.style === 'outline'
                ? 'border border-slate-300 text-slate-700 hover:border-blue-500 hover:text-blue-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {item.label}
        </a>
      ))}
    </div>
  );

  return (
    <>
    {announcement && section.heading && <div className="bg-blue-600 px-4 py-2 text-center text-xs font-medium text-white">{section.heading}</div>}
    <header className={`relative z-20 bg-white px-5 sm:px-8 ${variant === 'Minimal Header' ? 'py-2' : 'border-b border-slate-200 py-3'}`} style={section.styles?.section || {}}>
      <div className={`mx-auto flex max-w-7xl flex-wrap items-center ${mobileFirst ? 'gap-2' : 'gap-4'} ${centeredBrand ? 'justify-center' : 'justify-between'}`}>
        <a href="#" aria-label={section.brandName || 'Home'} className={`flex min-h-10 shrink-0 items-center ${centeredBrand ? 'w-full justify-center' : ''}`}>{logo}</a>
        <button
          type="button"
          className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-700 md:hidden"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(open => !open)}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        {!centeredBrand && renderNavigation(false)}
        {!centeredBrand && renderActions(false)}
        {centeredBrand && <div className="hidden w-full justify-center md:flex">{renderNavigation(false)}</div>}
        {centeredBrand && renderActions(false)}
        {menuOpen && <div className="w-full border-t border-slate-100 pt-3 md:hidden">{renderNavigation(true)}</div>}
      </div>
    </header>
    </>
  );
}

function SectionHeading({ section, light = false, centered = false }) {
  return (
    <div className={`mb-10 max-w-2xl ${centered ? 'mx-auto text-center' : ''}`}>
      {section.kicker && <p className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] ${light ? 'text-cyan-300' : 'text-blue-700'}`}>{section.kicker}</p>}
      <h2 className={`text-3xl font-bold tracking-tight sm:text-4xl ${light ? 'text-white' : 'text-slate-950'}`} style={section.styles?.heading || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} />
      {section.subtext && <div className={`rich-text-content mt-4 text-base leading-7 ${light ? 'text-slate-300' : 'text-slate-600'}`} style={section.styles?.description || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} />}
    </div>
  );
}

function HeroArtwork({ section, centered = false }) {
  if (!section.mediaUrl) return null;
  return (
    <div className={`relative mx-auto w-full ${centered ? 'max-w-3xl' : 'max-w-xl'}`}>
      <img
        src={normalizeSafeUrl(section.mediaUrl)}
        alt={section.mediaAlt || ''}
        className="mx-auto max-h-[28rem] w-full object-contain drop-shadow-2xl"
        style={section.styles?.media || {}}
      />
    </div>
  );
}

function HeroSection({ section, alternate }) {
  return alternate ? (
    <section className="relative overflow-hidden bg-slate-950 px-6 py-24 text-center text-white sm:py-32" style={section.styles?.section || {}}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/70 via-slate-950 to-slate-950" />
      <div className="relative mx-auto max-w-4xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-4 py-2 text-xs font-semibold text-blue-100"><Sparkles className="h-4 w-4" />{section.kicker || 'Cloud hosting, made simpler'}</span>
        <h1 className="mx-auto mt-7 max-w-4xl text-4xl font-bold tracking-tight sm:text-6xl" style={section.styles?.heading || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} />
        <div className="rich-text-content mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-300" style={section.styles?.description || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} />
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <ActionLink href={section.primaryUrl || '#features'}>{section.primaryText || 'Explore hosting'}</ActionLink>
          <ActionLink href={section.secondaryUrl || '#contact'} secondary>{section.secondaryText || 'Talk to our team'}</ActionLink>
        </div>
        <div className="mt-12"><HeroArtwork section={section} centered /></div>
      </div>
    </section>
  ) : (
    <section className="overflow-hidden bg-slate-950 px-6 py-20 text-white sm:py-28" style={section.styles?.section || {}}>
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_.95fr]">
        <div>
          <p className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-300"><Cloud className="h-4 w-4" />{section.kicker || 'Cloud hosting, made simpler'}</p>
          <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-6xl" style={section.styles?.heading || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.heading || '') }} />
          <div className="rich-text-content mt-6 max-w-2xl text-lg leading-8 text-slate-300" style={section.styles?.description || {}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} />
          <div className="mt-8 flex flex-wrap gap-3"><ActionLink href={section.primaryUrl || '#features'}>{section.primaryText || 'Explore hosting'}</ActionLink><ActionLink href={section.secondaryUrl || '#contact'} secondary>{section.secondaryText || 'Talk to our team'}</ActionLink></div>
        </div>
        <HeroArtwork section={section} />
      </div>
    </section>
  );
}

function BenefitSection({ section, alternate }) {
  const cards = section.cards || [];
  return alternate ? (
    <section id="why-us" className="bg-white px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
        <div><SectionHeading section={section} /><p className="max-w-lg text-sm leading-7 text-slate-600">Keep the important work visible, reduce unnecessary handoffs, and give your customers a storefront that feels thoughtfully put together.</p><a href="#features" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">See the platform <ArrowRight className="h-4 w-4" /></a></div>
        <div className="divide-y divide-slate-200 border-y border-slate-200" style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className="grid gap-3 py-6 sm:grid-cols-[52px_1fr]" style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} className="max-h-40 w-full rounded-lg object-cover sm:col-span-2" />        <CardIcon card={card} className="flex items-center justify-center rounded-xl bg-blue-50 text-2xl" /><div><h3 className="font-semibold text-slate-950">{card.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{cardText(card)}</p>{card.linkText && <a href={normalizeSafeUrl(card.linkUrl || '#features')} className="mt-3 inline-flex text-sm font-semibold text-blue-700">{card.linkText}<ArrowRight className="ml-1 h-4 w-4" /></a>}</div></article>)}</div>
      </div>
    </section>
  ) : (
    <section id="why-us" className="bg-slate-50 px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><SectionHeading section={section} centered />
        <div className="grid gap-5 md:grid-cols-3" style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg" style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} /><CardIcon card={card} className="flex items-center justify-center rounded-xl bg-blue-50 text-2xl" /><h3 className="mt-6 text-lg font-semibold text-slate-950">{card.title}</h3><p className="mt-3 text-sm leading-7 text-slate-600">{cardText(card)}</p><a href={normalizeSafeUrl(card.linkUrl || '#features')} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">{card.linkText || 'Learn more'} <ArrowRight className="h-4 w-4" /></a></article>)}</div>
      </div>
    </section>
  );
}

function FeaturesSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section id="features" className="bg-white px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><SectionHeading section={section} centered={!alternate} />
        {alternate ? <div className="mx-auto max-w-4xl divide-y divide-slate-200 border-y border-slate-200" style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className="grid gap-5 py-7 sm:grid-cols-[64px_1fr_auto] sm:items-start" style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} className="max-h-40 w-full rounded-lg object-cover sm:col-span-3" /><CardIcon card={card} className="flex items-center justify-center rounded-full bg-blue-50 text-2xl" /><div><h3 className="font-semibold text-slate-950">{card.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{cardText(card)}</p><ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">{(card.list || []).map(item => <li key={item} className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-600" />{item}</li>)}</ul>{card.linkText && <a href={normalizeSafeUrl(card.linkUrl || '#features')} className="mt-3 inline-flex text-sm font-semibold text-blue-700">{card.linkText}</a>}</div><span className="text-sm font-bold text-blue-700">0{i + 1}</span></article>)}</div>
          : <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className={`rounded-2xl border border-slate-200 p-6 ${i === 0 ? 'bg-slate-950 text-white md:col-span-2 lg:col-span-2' : 'bg-slate-50 text-slate-950'}`} style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} /><CardIcon card={card} className={`flex h-11 w-11 items-center justify-center rounded-xl ${i === 0 ? 'bg-white/10' : 'bg-white'} text-2xl`} /><h3 className="mt-5 text-lg font-semibold">{card.title}</h3><p className={`mt-2 text-sm leading-6 ${i === 0 ? 'text-slate-300' : 'text-slate-600'}`}>{cardText(card)}</p><ul className="mt-5 grid gap-2 sm:grid-cols-2">{(card.list || []).map(item => <li key={item} className={`flex items-center gap-2 text-xs ${i === 0 ? 'text-slate-300' : 'text-slate-600'}`}><Check className="h-3.5 w-3.5 text-emerald-500" />{item}</li>)}</ul>{card.linkText && <a href={normalizeSafeUrl(card.linkUrl || '#features')} className="mt-4 inline-flex text-sm font-semibold text-blue-700">{card.linkText}</a>}</article>)}</div>}
      </div>
    </section>
  );
}

function PricingSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section id="pricing" className="bg-slate-50 px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><SectionHeading section={section} centered={!alternate} />
        <div className={`grid gap-5 ${alternate ? 'lg:grid-cols-1' : 'md:grid-cols-3'}`} style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className={`relative rounded-2xl border p-7 ${card.featured ? 'border-blue-600 bg-white shadow-xl shadow-blue-900/10 ring-1 ring-blue-600' : 'border-slate-200 bg-white shadow-sm'}`} style={section.styles?.[`card-${i}`] || {}}>
          {card.featured && <span className="absolute -top-3 left-6 rounded-full bg-blue-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">Recommended</span>}
          <CardImage card={card} />
          <div className="flex flex-wrap items-start justify-between gap-4"><div><h3 className="text-lg font-semibold text-slate-950">{card.title}</h3><p className="mt-2 max-w-md text-sm leading-6 text-slate-600">{cardText(card)}</p></div><p className="text-xl font-bold text-slate-950">{card.price} <span className="text-xs font-normal text-slate-500">{card.period}</span></p></div>
          <ul className="mt-6 grid gap-3 sm:grid-cols-3">{(card.list || []).map(item => <li key={item} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{item}</li>)}</ul>
          <a href={normalizeSafeUrl(card.linkUrl || '#contact')} className={`mt-7 inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold ${card.featured ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border border-slate-300 text-slate-800 hover:bg-slate-50'}`}>{card.buttonText || 'Discuss this option'} <ArrowRight className="ml-2 h-4 w-4" /></a>
        </article>)}</div>
      </div>
    </section>
  );
}

function TestimonialsSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section className="bg-slate-950 px-6 py-20 text-white sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><SectionHeading section={section} light centered={!alternate} />
        {alternate && cards[0] ? <div className="grid gap-5 lg:grid-cols-[1.2fr_.8fr]" style={section.styles?.cards}><article className="rounded-3xl border border-white/10 bg-white/[0.06] p-8 sm:p-12" style={section.styles?.['card-0'] || {}}><CardImage card={cards[0]} /><span className="text-xs font-bold uppercase tracking-widest text-cyan-300">Customer story · replace with approved quote</span><p className="mt-6 text-2xl font-semibold leading-relaxed">“{cardText(cards[0])}”</p><p className="mt-7 text-sm text-slate-300">{cards[0].author} · {cards[0].role}</p></article><div className="grid gap-4">{cards.slice(1).map((card, i) => <article key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" style={section.styles?.[`card-${i + 1}`] || {}}><CardImage card={card} /><p className="text-xs font-bold text-cyan-300">Customer story · approved content required</p><h3 className="mt-3 font-semibold">{card.title}</h3><p className="mt-2 text-sm leading-6 text-slate-300">{cardText(card)}</p></article>)}</div></div>
          : <div className="grid gap-5 md:grid-cols-3" style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7" style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} /><span className="text-3xl text-cyan-300">“</span><h3 className="mt-3 font-semibold">{card.title}</h3><p className="mt-3 text-sm leading-7 text-slate-300">{cardText(card)}</p><div className="mt-6 border-t border-white/10 pt-4"><p className="text-sm font-semibold">{card.author}</p><p className="mt-1 text-xs text-slate-400">{card.role} · approved customer quote required</p></div></article>)}</div>}
      </div>
    </section>
  );
}

function TrustSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section className="bg-white px-6 py-16 sm:py-20" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><SectionHeading section={section} centered={!alternate} />
        <div className={`grid gap-4 ${alternate ? 'md:grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`} style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className={`flex items-start gap-4 rounded-2xl border border-slate-200 p-6 ${alternate ? 'bg-slate-50' : 'bg-white'}`} style={section.styles?.[`card-${i}`] || {}}><CardImage card={card} className="max-h-24 w-24 rounded-lg object-cover" /><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><ShieldCheck className="h-5 w-5" /></span><div><h3 className="font-semibold text-slate-950">{card.title}</h3><p className="mt-1 text-sm leading-6 text-slate-600">{cardText(card)}</p>{card.linkText && <a href={normalizeSafeUrl(card.linkUrl || '#contact')} className="mt-2 inline-flex text-sm font-semibold text-blue-700">{card.linkText}</a>}</div></article>)}</div>
      </div>
    </section>
  );
}

function FAQSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section id="faq" className="bg-slate-50 px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className={`mx-auto max-w-7xl ${alternate ? 'grid gap-12 lg:grid-cols-[.75fr_1.25fr]' : ''}`}>
        <SectionHeading section={section} centered={!alternate} />
        <div className="divide-y divide-slate-200 border-y border-slate-200" style={section.styles?.cards || {}}>{cards.map((card, i) => <details key={i} className="group py-5" style={section.styles?.[`card-${i}`] || {}}><summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-slate-900"><span>{card.question}</span><ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" /></summary><p className="max-w-3xl pt-3 text-sm leading-7 text-slate-600">{card.answer}</p></details>)}</div>
      </div>
    </section>
  );
}

function BlogSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <section className="bg-white px-6 py-20 sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><div className="flex flex-wrap items-end justify-between gap-5"><SectionHeading section={section} /><a href="#contact" className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">More insights <ArrowRight className="h-4 w-4" /></a></div>
        <div className={`grid gap-5 ${alternate ? 'lg:grid-cols-1' : 'md:grid-cols-3'}`} style={section.styles?.cards || {}}>{cards.map((card, i) => <article key={i} className={`group overflow-hidden rounded-2xl border border-slate-200 bg-white ${alternate ? 'grid sm:grid-cols-[180px_1fr]' : ''}`} style={section.styles?.[`card-${i}`] || {}}><div className={`flex items-end ${card.image ? '' : `bg-gradient-to-br ${i % 2 ? 'from-cyan-700 to-blue-950' : 'from-blue-700 to-indigo-950'}`} p-5 text-white ${alternate ? 'min-h-36' : 'min-h-44'}`} style={card.image ? { backgroundImage: `linear-gradient(to top, rgba(15,23,42,.65), transparent), url("${normalizeSafeUrl(card.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}><span className="text-xs font-bold uppercase tracking-widest">{card.category || 'Insights'}</span></div><div className="p-6"><p className="text-xs font-medium text-slate-500">{card.date}</p><h3 className="mt-3 text-lg font-semibold leading-snug text-slate-950">{card.title}</h3><p className="mt-3 text-sm leading-6 text-slate-600">{cardText(card)}</p><a href={normalizeSafeUrl(card.linkUrl || '#contact')} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">{card.linkText || 'Read article'} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></a></div></article>)}</div>
      </div>
    </section>
  );
}

function SliderSection({ section, alternate }) {
  const cards = section.cards || [];
  const [active, setActive] = useState(0);
  const current = cards[active % Math.max(cards.length, 1)];
  if (!current) return null;
  return (
    <section className="bg-slate-900 px-6 py-20 text-white sm:py-24" style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl"><div className="grid items-center gap-10 lg:grid-cols-[1fr_.85fr]"><div><SectionHeading section={section} light /><CardImage card={current} className="my-4 max-h-60 w-full rounded-xl object-cover" /><CardIcon card={current} className="flex items-center justify-center text-3xl" /><h3 className="mt-5 text-2xl font-bold">{current.title}</h3><p className="mt-3 max-w-xl text-sm leading-7 text-slate-300">{cardText(current)}</p><ActionLink href={current.linkUrl || section.primaryUrl || '#features'}>{current.buttonText || section.primaryText || 'Explore the platform'}</ActionLink><div className="mt-8 flex items-center gap-3"><button type="button" aria-label="Previous slide" onClick={() => setActive(index => (index - 1 + cards.length) % cards.length)} className="rounded-full border border-white/20 p-2 hover:bg-white/10"><ArrowLeft className="h-4 w-4" /></button><span className="text-xs text-slate-400">{String(active + 1).padStart(2, '0')} / {String(cards.length).padStart(2, '0')}</span><button type="button" aria-label="Next slide" onClick={() => setActive(index => (index + 1) % cards.length)} className="rounded-full border border-white/20 p-2 hover:bg-white/10"><ArrowRight className="h-4 w-4" /></button></div></div><div className={`grid gap-3 ${alternate ? 'sm:grid-cols-2' : ''}`} style={section.styles?.cards || {}}>{cards.map((card, index) => <button key={index} type="button" onClick={() => setActive(index)} className={`rounded-2xl border p-6 text-left transition ${active === index ? 'border-blue-400 bg-blue-500/15' : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]'}`} style={section.styles?.[`card-${index}`] || {}}><CardImage card={card} className="max-h-28 w-full rounded-lg object-cover" /><CardIcon card={card} className="flex items-center justify-center text-2xl" /><h4 className="mt-4 font-semibold">{card.title}</h4><p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-400">{cardText(card)}</p></button>)}</div></div></div>
    </section>
  );
}

function CTASection({ section, alternate }) {
  return alternate ? (
    <section id="get-started" className="bg-slate-50 px-6 py-16 sm:py-20" style={section.styles?.section || {}}><div data-storefront-transparent-surface className="mx-auto flex max-w-7xl flex-col justify-between gap-8 rounded-3xl bg-blue-700 p-8 text-white sm:p-12 lg:flex-row lg:items-center"><div className="max-w-2xl"><p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-200">{section.kicker || 'Your next step'}</p><h2 className="mt-3 text-3xl font-bold sm:text-4xl">{section.heading}</h2><div className="rich-text-content mt-4 leading-7 text-blue-100" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} /></div><ActionLink href={section.primaryUrl || '#contact'}>{section.primaryText || 'Talk to our team'}</ActionLink></div></section>
  ) : (
    <section id="contact" className="bg-slate-950 px-6 py-20 text-center text-white sm:py-24" style={section.styles?.section || {}}><div data-storefront-transparent-surface className="mx-auto max-w-4xl rounded-3xl border border-white/10 bg-gradient-to-br from-blue-900 to-slate-900 px-7 py-14 shadow-2xl sm:px-14"><p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">{section.kicker || 'Let’s get started'}</p><h2 className="mt-4 text-3xl font-bold sm:text-5xl">{section.heading}</h2><div className="rich-text-content mx-auto mt-5 max-w-2xl leading-7 text-slate-300" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} /><div className="mt-8"><ActionLink href={section.primaryUrl || '#contact'}>{section.primaryText || 'Talk to our team'}</ActionLink></div></div></section>
  );
}

function FooterSection({ section, alternate }) {
  const cards = section.cards || [];
  return (
    <footer id="contact" className={`px-6 text-white ${alternate ? 'bg-slate-950 py-8' : 'bg-slate-950 py-14'}`} style={section.styles?.section || {}}>
      <div className="mx-auto max-w-7xl">
        <div className={`border-b border-white/10 pb-8 ${alternate ? 'flex flex-wrap items-center justify-between gap-8' : 'grid gap-10 md:grid-cols-[1.2fr_2fr]'}`}>
          <div className={alternate ? 'flex items-center gap-4' : ''}>
            <a href="#" className="inline-flex items-center gap-2 text-lg font-bold"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600"><Cloud className="h-4 w-4" /></span>{section.heading || 'CloudSetu'}</a>
            {!alternate && <div className="mt-4 max-w-sm text-sm leading-6 text-slate-400" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.subtext) }} />}
          </div>
          <div className={`grid gap-6 ${alternate ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'}`} style={section.styles?.cards || {}}>{cards.map((card, i) => <div key={i}><h3 className="text-sm font-semibold">{card.title}</h3><ul className="mt-4 space-y-3">{(card.text || '').split('\n').filter(Boolean).map(link => <li key={link}><a href={link.toLowerCase().includes('faq') ? '#faq' : '#contact'} className="text-sm text-slate-400 hover:text-white">{link}</a></li>)}</ul></div>)}</div>
        </div>
        <div className="flex flex-wrap justify-between gap-3 pt-6 text-xs text-slate-500"><span>© {new Date().getFullYear()} {section.heading || 'CloudSetu'}. All rights reserved.</span><span>Terms · Privacy</span></div>
      </div>
    </footer>
  );
}

// --- Products Section (auto-fetches platform catalog; content is not editable by resellers) ---
const BILLING_LABEL = { monthly: '/mo', quarterly: '/qtr', half_yearly: '/6mo', yearly: '/yr', two_years: '/2yr', three_years: '/3yr' };
const priceRange = product => {
  // Show the partner's own price (billing_prices_display) when the storefront API provides it.
  const priceMap = (product.billing_prices_display && Object.keys(product.billing_prices_display).length)
    ? product.billing_prices_display
    : (product.billing_prices || {});
  const prices = Object.entries(priceMap).filter(([, v]) => v > 0).map(([, v]) => v);
  if (!prices.length && (product.display_price || product.base_price)) return `\u20b9${product.display_price || product.base_price}`;
  if (!prices.length) return 'Contact us';
  const min = Math.min(...prices);
  const cycle = Object.entries(priceMap).find(([, v]) => v === min)?.[0] || 'monthly';
  return `\u20b9${min}${BILLING_LABEL[cycle] || ''}`;
};

function ProductCard({ product, highlight = false, onOrder }) {
  return (
    <div className={`rounded-2xl border flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${highlight ? 'border-blue-500 shadow-blue-100 bg-white' : 'border-slate-200 bg-white hover:border-blue-200'}`}>
      <div className="p-5 flex-1">
        {highlight && <span className="inline-block text-[10px] font-bold tracking-widest uppercase text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 mb-3">Featured</span>}
        <span className="inline-block text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 mb-2">{product.category}</span>
        <h4 className="text-base font-bold text-slate-900 mb-1 leading-tight">{product.name}</h4>
        <p className="text-xs text-slate-500 mb-3 line-clamp-2">{product.description}</p>
        <ul className="space-y-1 mb-4">
          {(product.features || []).slice(0, 4).map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700"><Check className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>
          ))}
        </ul>
      </div>
      <div className="px-5 pb-5">
        <div className="text-xl font-extrabold text-slate-900 mb-3">{priceRange(product)}</div>
        <button type="button" onClick={() => onOrder ? onOrder(product) : (document.getElementById('contact')?.scrollIntoView({behavior:'smooth'}) || null)} className="block text-center w-full rounded-lg bg-blue-600 text-white text-sm font-semibold py-2 hover:bg-blue-500 transition">Get Started</button>
      </div>
    </div>
  );
}

function ProductsSection({ section, alternate, onOrder, products: productsProp }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [expanded, setExpanded] = useState({});
  const variant = section.variant || 'Product Grid';

  useEffect(() => {
    // On a reseller's public storefront the page passes in that reseller's own priced product list.
    if (Array.isArray(productsProp) && productsProp.length) {
      const activeProvided = productsProp.filter(p => p.is_active !== false);
      setProducts(activeProvided);
      setActiveCategory([...new Set(activeProvided.map(p => p.category))][0] || '');
      setLoading(false);
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/products`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.products || [];
        const active = list.filter(p => p.is_active !== false);
        setProducts(active);
        const cats = [...new Set(active.map(p => p.category))];
        setActiveCategory(cats[0] || '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [productsProp]);

  const bgClass = alternate ? 'bg-slate-50' : 'bg-white';
  const categories = [...new Set(products.map(p => p.category))];

  const sectionHeader = (
    <div className="text-center mb-10">
      <p className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-2">{section.kicker || 'What We Offer'}</p>
      <h2 className="text-3xl font-extrabold text-slate-900 mb-3">{section.heading || 'Our Products & Services'}</h2>
      <p className="text-slate-500 max-w-xl mx-auto text-sm">{section.subtext || 'Choose from our range of cloud services and SaaS solutions.'}</p>
    </div>
  );

  const skeleton = Array(3).fill(0).map((_, i) => (
    <div key={i} className="rounded-2xl border border-slate-100 bg-white p-5 animate-pulse">
      <div className="h-3 bg-slate-200 rounded w-1/4 mb-3" />
      <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
      <div className="h-3 bg-slate-100 rounded w-full mb-1" />
      <div className="h-3 bg-slate-100 rounded w-5/6 mb-6" />
      <div className="h-8 bg-slate-200 rounded w-1/3 mb-4" />
      <div className="h-9 bg-blue-100 rounded w-full" />
    </div>
  ));

  if (loading) return (
    <section className={`${bgClass} py-16 px-6`}>
      <div className="max-w-6xl mx-auto">{sectionHeader}<div className="grid grid-cols-1 md:grid-cols-3 gap-5">{skeleton}</div></div>
    </section>
  );

  if (!products.length) return (
    <section className={`${bgClass} py-16 px-6`}>
      <div className="max-w-6xl mx-auto text-center">
        {sectionHeader}
        <p className="text-slate-400 text-sm">No products available yet. Add products in Admin \u2192 Products.</p>
      </div>
    </section>
  );

  if (variant === 'Product Grid') return (
    <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
      <div className="max-w-6xl mx-auto">
        {sectionHeader}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p, i) => <ProductCard key={p.id || i} product={p} highlight={i === 0} onOrder={onOrder} />)}
        </div>
      </div>
    </section>
  );

  if (variant === 'Featured Spotlight') {
    const [featured, ...rest] = products;
    return (
      <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
        <div className="max-w-6xl mx-auto">
          {sectionHeader}
          <div className="rounded-2xl bg-gradient-to-r from-blue-700 to-indigo-700 p-8 text-white mb-8 flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-1">
              <span className="text-xs font-bold tracking-widest uppercase text-blue-200">{featured.category}</span>
              <h3 className="text-2xl font-extrabold mt-1 mb-2">{featured.name}</h3>
              <p className="text-blue-100 text-sm mb-4">{featured.description}</p>
              <ul className="grid grid-cols-2 gap-1 mb-6">
                {(featured.features || []).slice(0, 6).map((f, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-blue-100"><Check className="h-3 w-3 text-green-300 flex-shrink-0" />{f}</li>
                ))}
              </ul>
              <a href="#contact" className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-blue-50 transition">Get Started<ArrowUpRight className="h-4 w-4" /></a>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-4xl font-black">{priceRange(featured)}</div>
              <div className="text-blue-200 text-xs mt-1">Starting price</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p, i) => <ProductCard key={p.id || i} product={p} onOrder={onOrder} />)}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'Category Tabs') {
    const filtered = products.filter(p => p.category === activeCategory);
    return (
      <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
        <div className="max-w-6xl mx-auto">
          {sectionHeader}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {categories.map(cat => (
              <button key={cat} type="button" onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${activeCategory === cat ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => <ProductCard key={p.id || i} product={p} highlight={i === 0} onOrder={onOrder} />)}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'Pricing Cards') return (
    <section className={`${alternate ? 'bg-slate-900' : 'bg-gradient-to-b from-slate-900 to-slate-800'} py-16 px-6`} style={section.styles?.section || {}}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase text-blue-400 mb-2">{section.kicker || 'Transparent Pricing'}</p>
          <h2 className="text-3xl font-extrabold text-white mb-3">{section.heading || 'Simple, Transparent Pricing'}</h2>
          <p className="text-slate-400 max-w-xl mx-auto text-sm">{section.subtext || 'Pick the right plan for your business.'}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p, i) => (
            <div key={p.id || i} className={`rounded-2xl p-6 flex flex-col border ${i === 1 ? 'border-blue-500 bg-blue-600 text-white' : 'border-slate-700 bg-slate-800 text-white'}`}>
              {i === 1 && <span className="text-[10px] font-bold tracking-widest uppercase text-blue-200 bg-blue-500 rounded-full px-2 py-0.5 self-start mb-3">Most Popular</span>}
              <span className="text-xs text-slate-400 mb-1">{p.category}</span>
              <h4 className="text-lg font-bold mb-1">{p.name}</h4>
              <p className="text-xs text-slate-400 mb-4 flex-1">{p.description}</p>
              <div className="text-3xl font-black mb-4">{priceRange(p)}</div>
              <ul className="space-y-1.5 mb-5">
                {(p.features || []).slice(0, 5).map((f, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-xs"><Check className={`h-3.5 w-3.5 flex-shrink-0 ${i === 1 ? 'text-green-300' : 'text-green-500'}`} />{f}</li>
                ))}
              </ul>
              <button type="button" onClick={() => onOrder ? onOrder(p) : null} className={`block w-full text-center rounded-lg font-semibold text-sm py-2.5 transition ${i === 1 ? 'bg-white text-blue-700 hover:bg-blue-50' : 'bg-slate-700 text-white hover:bg-slate-600'}`}>Get Started</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  if (variant === 'Compact List') return (
    <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
      <div className="max-w-4xl mx-auto">
        {sectionHeader}
        <div className="space-y-3">
          {products.map((p, i) => (
            <div key={p.id || i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <button type="button" onClick={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2 py-0.5 flex-shrink-0">{p.category}</span>
                  <span className="font-semibold text-slate-800 truncate">{p.name}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-bold text-slate-900 text-sm">{priceRange(p)}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded[i] ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {expanded[i] && (
                <div className="px-5 pb-4 border-t border-slate-100">
                  <p className="text-sm text-slate-600 mt-3 mb-3">{p.description}</p>
                  <ul className="grid grid-cols-2 gap-1 mb-3">
                    {(p.features || []).map((f, fi) => (
                      <li key={fi} className="flex items-center gap-1.5 text-xs text-slate-700"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <a href="#contact" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">Get Started <ArrowUpRight className="h-3.5 w-3.5" /></a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  if (variant === 'Comparison Table') {
    const allFeatures = [...new Set(products.flatMap(p => p.features || []))].slice(0, 8);
    return (
      <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
        <div className="max-w-6xl mx-auto">
          {sectionHeader}
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-4 font-semibold text-slate-700 bg-slate-50 w-40">Feature</th>
                  {products.slice(0, 4).map((p, i) => (
                    <th key={p.id || i} className={`text-center p-4 font-bold ${i === 0 ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-800'}`}>
                      <div>{p.name}</div>
                      <div className={`text-xs font-normal mt-0.5 ${i === 0 ? 'text-blue-100' : 'text-slate-500'}`}>{priceRange(p)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, fi) => (
                  <tr key={fi} className={fi % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="p-4 text-slate-600 text-xs">{feature}</td>
                    {products.slice(0, 4).map((p, i) => (
                      <td key={p.id || i} className="text-center p-4">
                        {(p.features || []).includes(feature)
                          ? <Check className="h-4 w-4 text-green-500 mx-auto" />
                          : <span className="text-slate-200">\u2014</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-slate-200">
                  <td className="p-4" />
                  {products.slice(0, 4).map((p, i) => (
                    <td key={p.id || i} className="text-center p-4">
                      <button type="button" onClick={() => onOrder ? onOrder(products[i]) : null} className={`inline-block rounded-lg text-xs font-bold px-4 py-2 transition ${i === 0 ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>Get Started</button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`${bgClass} py-16 px-6`} style={section.styles?.section || {}}>
      <div className="max-w-6xl mx-auto">
        {sectionHeader}
        {categories.map(cat => (
          <div key={cat} className="mb-8">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <span className="flex-1 h-px bg-slate-200" />{cat}<span className="flex-1 h-px bg-slate-200" />
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.filter(p => p.category === cat).map((p, i) => <ProductCard key={p.id || i} product={p} onOrder={onOrder} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function StorefrontSection({ section, onOrder, products }) {
  const breakpoint = useResponsiveBreakpoint();
  const sectionRef = useRef(null);
  const responsiveStyles = getSectionStylesForBreakpoint(section, breakpoint);
  const scrollAnimation = responsiveStyles.section?.scrollAnimation || '';
  const hoverAnimation = responsiveStyles.section?.hoverAnimation || '';
  const responsiveSection = {
    ...section,
    styles: Object.fromEntries(Object.entries(responsiveStyles).map(([target, style]) => {
      return [target, Object.fromEntries(Object.entries(style).filter(([property]) => property !== 'scrollAnimation' && property !== 'hoverAnimation'))];
    })),
  };
  useEffect(() => {
    const element = sectionRef.current;
    if (!element || !scrollAnimation) return undefined;
    const revealWhenVisible = () => {
      const { top, bottom } = element.getBoundingClientRect();
      if (top >= window.innerHeight * 0.88 || bottom <= 0) return;
      element.dataset.scrollRevealed = 'true';
      window.removeEventListener('scroll', revealWhenVisible);
      window.removeEventListener('resize', revealWhenVisible);
    };
    revealWhenVisible();
    window.addEventListener('scroll', revealWhenVisible, { passive: true });
    window.addEventListener('resize', revealWhenVisible);
    return () => {
      window.removeEventListener('scroll', revealWhenVisible);
      window.removeEventListener('resize', revealWhenVisible);
    };
  }, [scrollAnimation]);

  const alternate = isAlternate(responsiveSection.variant);
  let content = null;
  if (responsiveSection.type === 'header') content = <HeaderSection section={responsiveSection} />;
  else if (responsiveSection.type === 'hero') content = <HeroSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'why-us') content = <BenefitSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'features') content = <FeaturesSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'pricing') content = <PricingSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'testimonials') content = <TestimonialsSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'trust') content = <TrustSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'faq') content = <FAQSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'blog') content = <BlogSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'slider') content = <SliderSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'cta') content = <CTASection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'footer') content = <FooterSection section={responsiveSection} alternate={alternate} />;
  else if (responsiveSection.type === 'products') content = <ProductsSection section={responsiveSection} alternate={alternate} onOrder={onOrder} products={products} />;
  if (!content) return null;
  return (
    <div
      ref={sectionRef}
      className="storefront-section-motion"
      data-storefront-section-type={responsiveSection.type}
      data-scroll-animation={scrollAnimation || undefined}
      data-scroll-revealed={scrollAnimation ? 'false' : 'true'}
      data-hover-animation={hoverAnimation || undefined}
    >
      {content}
    </div>
  );
}
